import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, Moon, Sun, X } from "lucide-react";
import { IMAGES, NAV_LINKS, SERVICE_LINKS } from "../../data/content";
import { useSite } from "../../context/SiteContext";

const Navbar = () => {
  const { openBooking, scrollToId, dark, toggleDark } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    const wasOpen = mobileOpen;
    setMobileOpen(false);
    setServicesOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else if (wasOpen) {
      setTimeout(() => scrollToId(id), 420);
    } else {
      scrollToId(id);
    }
  };

  const goService = (s) => {
    setServicesOpen(false);
    setMobileOpen(false);
    if (s.to) navigate(s.to);
    else go(s.id);
  };

  const linkClass =
    "text-[13px] font-semibold text-slate-600 dark:text-slate-300 hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300 whitespace-nowrap";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "glass shadow-[0_8px_40px_-16px_rgba(42,49,146,0.25)]" : "bg-transparent border border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between gap-6">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-2.5 shrink-0"
          aria-label="Medlyn 360 home"
        >
          <img src={IMAGES.logoMark} alt="" className="h-10 w-auto dark:brightness-0 dark:invert" />
          <img src={IMAGES.logoWordmark} alt="Medlyn 360" className="h-7 sm:h-8 w-auto dark:brightness-0 dark:invert" />
        </button>
        <div className="hidden lg:flex items-center gap-6 xl:gap-7">
          <button data-testid="nav-link-home" onClick={() => go("home")} className={linkClass}>Home</button>
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              data-testid="nav-services-button"
              onClick={() => setServicesOpen((o) => !o)}
              aria-expanded={servicesOpen}
              className={`${linkClass} inline-flex items-center gap-1`}
            >
              Services <ChevronDown size={13} className={`transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3"
                  data-testid="nav-services-dropdown"
                >
                  <div className="w-80 rounded-2xl bg-white dark:bg-[#0D1434] border border-slate-100 dark:border-white/10 shadow-[0_30px_70px_-30px_rgba(42,49,146,0.4)] p-2">
                    {SERVICE_LINKS.map((s) => (
                      <button
                        key={s.label}
                        data-testid={`nav-services-${s.to ? "dental" : "medical"}`}
                        onClick={() => goService(s)}
                        className="w-full text-left rounded-xl px-4 py-3.5 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors duration-200"
                      >
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{s.label}</p>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{s.desc}</p>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {NAV_LINKS.slice(1).map((l) => (
            <button key={l.id} data-testid={`nav-link-${l.id}`} onClick={() => go(l.id)} className={linkClass}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle-button"
            onClick={toggleDark}
            aria-label="Toggle dark mode"
            className="p-2.5 rounded-full border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:border-brand-sky hover:text-brand-navy dark:hover:text-brand-sky transition-colors duration-300"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            data-testid="nav-book-call-button"
            onClick={openBooking}
            className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink text-sm font-bold px-6 py-2.5 hover:shadow-[0_12px_30px_-10px_rgba(42,49,146,0.6)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
          >
            <span className="hidden xl:inline">Book a Consultation</span>
            <span className="xl:hidden">Book a Call</span>
          </button>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Open menu"
            className="lg:hidden p-2.5 rounded-full border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden glass overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <button data-testid="mobile-nav-link-home" onClick={() => go("home")} className="text-left text-sm font-semibold text-slate-700 dark:text-slate-200">
                Home
              </button>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Services</p>
              {SERVICE_LINKS.map((s) => (
                <button
                  key={s.label}
                  data-testid={`mobile-nav-services-${s.to ? "dental" : "medical"}`}
                  onClick={() => goService(s)}
                  className="text-left text-sm font-semibold text-slate-700 dark:text-slate-200 pl-4 border-l-2 border-brand-sky/40"
                >
                  {s.label}
                </button>
              ))}
              {NAV_LINKS.slice(1).map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-book-call-button"
                onClick={() => { setMobileOpen(false); openBooking(); }}
                className="mt-2 rounded-full bg-brand-navy dark:bg-brand-sky text-white dark:text-brand-ink text-sm font-bold px-6 py-3"
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
